import { canv } from "@/canv/global";
import {
    easeOutCubic,
    getColorProperty,
    getCubicBezierXYatT,
    getNumberProperty,
    getStringProperty,
} from "@/canv/utils";
import { ElementBase } from "@/elementBase";
import { LinePoint } from "@/elements/arrow/LinePoint";
import { LineTextItem } from "@/elements/arrow/LineTextItem";
import { QuadraticLine } from "@/elements/arrow/QuadraticLine";
import type { TextWidgetV2 } from "@/elements/text/textV2";
import { fastActionManager } from "@/tools/cursorTool";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import type {
    ArrowPlace,
    BezierArea,
    ClosestPointResult,
    CurvePoint,
    ExtractText,
    LineDirection,
    SearchTextObject,
    WidgetStateMap,
} from "./types";

export class ArrowWidget extends ElementBase {
    bindFocusedTextAutosave(textPart: TextWidgetV2 | null) {
        if (!textPart) {
            return;
        }

        textPart.setEditingAutosaveHandler(() => {
            canv.eventManager.openRecord();
            this.saveState("textFocus");
            canv.eventManager.closeRecord();
            this.fixState("textFocus");
        });
    }

    constructor(firstPointX, firstPointY, lastPointX, lastPointY) {
        super();
        const firstPoint = new LinePoint(firstPointX, firstPointY);
        const lastPoint = new LinePoint(lastPointX, lastPointY);
        this.points.push(firstPoint);
        this.points.push(lastPoint);

        this.updateDims();

        this.customResize = true;
        this.rotatable = false;

        this.disableConnectors = true;
        this.disableLevelLines = true;
        this.disableOutline = true;
        this.fastAction = "";
    }

    updateDims(withBaseParent = true) {
        const firstPoint = this.getFirstPoint();
        let leftX = firstPoint.x;
        let rightX = firstPoint.x;
        let topY = firstPoint.y;
        let bottomY = firstPoint.y;

        for (let i = 0; i < this.points.length; i++) {
            leftX = this.points[i].x < leftX ? this.points[i].x : leftX;
            rightX = this.points[i].x > rightX ? this.points[i].x : rightX;
            topY = this.points[i].y < topY ? this.points[i].y : topY;
            bottomY = this.points[i].y > bottomY ? this.points[i].y : bottomY;
        }

        this.startX = leftX;
        this.startY = topY;
        this.width = rightX - leftX;
        this.height = bottomY - topY;

        if (this.lineType === "curved" || this.lineType === "straight") {
            this.calcControlPoints();
            this.shareRepositionTextItemsBezierMode();
            this.updateDimsBezierMode();
        } else if (this.lineType === "quadratic") {
            this.repositionQuadraticPoints();
            this.calcQuadraticLines();
            this.shareRepositionTextItemsQuadraticMode();
        }

        this.updateBase(this.startX, this.startY, this.width, this.height, withBaseParent);
    }

    updateDimsBezierMode() {
        const total = {
            minX: this.getFirstPoint().x,
            maxX: this.getFirstPoint().x,
            minY: this.getFirstPoint().y,
            maxY: this.getFirstPoint().y,
        };

        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return false;
            const p = this.points[i],
                cp = this.cpoints[i],
                cpp = this.cpoints[i - 1];

            // todo посмотреть внимательно, что if ничего не сломал
            if (cpp.cn && cp.cp) {
                const res = this.getBezierDims(
                    this.points[i - 1].x,
                    this.points[i - 1].y,
                    cpp.cn[0],
                    cpp.cn[1],
                    cp.cp[0],
                    cp.cp[1],
                    p.x,
                    p.y,
                );

                total.minX = res.minX < total.minX ? res.minX : total.minX;
                total.maxX = res.maxX > total.maxX ? res.maxX : total.maxX;
                total.minY = res.minY < total.minY ? res.minY : total.minY;
                total.maxY = res.maxY > total.maxY ? res.maxY : total.maxY;
            }
        }

        this.startX = total.minX;
        this.startY = total.minY;
        this.width = total.maxX - total.minX;
        this.height = total.maxY - total.minY;
    }

    calcControlPoints() {
        // Default tension of one-quarter gives nice results
        let tension = 0.000001;

        if (this.lineType === "curved") {
            tension = 0.25;
        }

        const points: number[][] = [];
        for (let i = 0; i < this.points.length; i++) {
            points.push([this.points[i].x, this.points[i].y]);
        }

        const l = points.length;

        if (l < 2) return [];

        function h(x, y) {
            return Math.sqrt(x * x + y * y);
        }

        const cpoints: CurvePoint[] = [];
        points.forEach(() => {
            cpoints.push({});
        });

        for (let i = 1; i < l - 1; i++) {
            const pi = points[i],
                pp = points[i - 1],
                pn = points[i + 1];

            let rdx = pn[0] - pp[0],
                rdy = pn[1] - pp[1],
                rd = h(rdx, rdy);

            if (rd === 0) rd = 1;

            const dx = rdx / rd;
            const dy = rdy / rd;

            const dp = h(pi[0] - pp[0], pi[1] - pp[1]),
                dn = h(pi[0] - pn[0], pi[1] - pn[1]);

            const cpx = pi[0] - dx * dp * tension,
                cpy = pi[1] - dy * dp * tension,
                cnx = pi[0] + dx * dn * tension,
                cny = pi[1] + dy * dn * tension;

            cpoints[i] = {
                cp: [cpx, cpy],
                cn: [cnx, cny],
            };
        }

        if (l === 2) {
            const k = this.lineWidth / 10;
            const normilizedK = easeOutCubic(k);
            const arrowSize = normilizedK * 30;
            const fixedGap = 9 * arrowSize; //canv.actionManager.getConstant(50);

            cpoints[0] = {
                cn: [points[0][0] + (points[1][0] - points[0][0]) / 2, points[0][1]],
            };
            if (this.firstPointPlace === "") {
                if (this.getFirstPoint().x <= this.getLastPoint().x) {
                    cpoints[0] = {
                        cn: [points[0][0] + fixedGap, points[0][1]],
                    };
                } else {
                    cpoints[0] = {
                        cn: [points[0][0] - fixedGap, points[0][1]],
                    };
                }
            } else if (this.firstPointPlace === "right") {
                cpoints[0] = {
                    cn: [points[0][0] + fixedGap, points[0][1]],
                };
            } else if (this.firstPointPlace === "left") {
                cpoints[0] = {
                    cn: [points[0][0] - fixedGap, points[0][1]],
                };
            } else if (this.firstPointPlace === "top") {
                cpoints[0] = {
                    cn: [points[0][0], points[0][1] - fixedGap],
                };
            } else if (this.firstPointPlace === "bottom") {
                cpoints[0] = {
                    cn: [points[0][0], points[0][1] + fixedGap],
                };
            }

            if (this.lastPointPlace === "") {
                if (this.getFirstPoint().x <= this.getLastPoint().x) {
                    cpoints[l - 1] = {
                        cp: [points[1][0] - fixedGap, points[1][1]],
                    };
                } else {
                    cpoints[l - 1] = {
                        cp: [points[1][0] + fixedGap, points[1][1]],
                    };
                }
            } else if (this.lastPointPlace === "right") {
                cpoints[l - 1] = {
                    cp: [points[1][0] + fixedGap, points[1][1]],
                };
            } else if (this.lastPointPlace === "left") {
                cpoints[l - 1] = {
                    cp: [points[1][0] - fixedGap, points[1][1]],
                };
            } else if (this.lastPointPlace === "top") {
                cpoints[l - 1] = {
                    cp: [points[1][0], points[1][1] - fixedGap],
                };
            } else if (this.lastPointPlace === "bottom") {
                cpoints[l - 1] = {
                    cp: [points[1][0], points[1][1] + fixedGap],
                };
            }

            if (this.lineType === "straight") {
                cpoints[0] = {
                    cn: [
                        points[0][0] + (points[1][0] - points[0][0]) / 2,
                        points[0][1] + (points[1][1] - points[0][1]) / 2,
                    ],
                };

                cpoints[l - 1] = {
                    cp: [
                        points[0][0] + (points[1][0] - points[0][0]) / 2,
                        points[0][1] + (points[1][1] - points[0][1]) / 2,
                    ],
                };
            }
        } else {
            cpoints[0] = {
                cn: [
                    (points[0][0] + cpoints[1].cp![0]) / 2,
                    (points[0][1] + cpoints[1].cp![1]) / 2,
                ],
            };

            cpoints[l - 1] = {
                cp: [
                    (points[l - 1][0] + cpoints[l - 2].cn![0]) / 2,
                    (points[l - 1][1] + cpoints[l - 2].cn![1]) / 2,
                ],
            };
        }

        this.cpoints = cpoints;
    }

    calcQuadraticLines() {
        this.quadraticLines = [];

        if (this.points.length === 2) {
            const firstPlaceType =
                this.firstPointPlace !== ""
                    ? this.firstPointPlace === "left" || this.firstPointPlace === "right"
                        ? "horizontal"
                        : "vertical"
                    : "horizontal";
            const lastPlaceType =
                this.lastPointPlace !== ""
                    ? this.lastPointPlace === "left" || this.lastPointPlace === "right"
                        ? "horizontal"
                        : "vertical"
                    : "horizontal";
            if (firstPlaceType === "horizontal") {
                if (lastPlaceType === "horizontal") {
                    const firstLine = new QuadraticLine(
                        this.getFirstPoint().x,
                        this.getFirstPoint().y,
                        this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        this.getFirstPoint().y,
                        "horizontal",
                    );
                    firstLine.potencial = {
                        x:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 4,
                        y: this.getFirstPoint().y,
                        xCreate:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        yCreate: this.getFirstPoint().y,
                    };
                    this.quadraticLines.push(firstLine);

                    const secondLine = new QuadraticLine(
                        this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        this.getFirstPoint().y,
                        this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        this.getLastPoint().y,
                        "vertical",
                    );
                    secondLine.potencial = {
                        x:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        y:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        xCreate:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        yCreate:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                    };
                    this.quadraticLines.push(secondLine);

                    const thirdLine = new QuadraticLine(
                        this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        this.getLastPoint().y,
                        this.getLastPoint().x,
                        this.getLastPoint().y,
                        "horizontal",
                    );
                    thirdLine.potencial = {
                        x:
                            this.getFirstPoint().x +
                            ((this.getLastPoint().x - this.getFirstPoint().x) / 4) * 3,
                        y: this.getLastPoint().y,
                        xCreate:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        yCreate: this.getLastPoint().y,
                    };
                    this.quadraticLines.push(thirdLine);
                } else {
                    const firstLine = new QuadraticLine(
                        this.getFirstPoint().x,
                        this.getFirstPoint().y,
                        this.getLastPoint().x,
                        this.getFirstPoint().y,
                        "horizontal",
                    );
                    firstLine.potencial = {
                        x:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        y: this.getFirstPoint().y,
                        xCreate:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        yCreate: this.getFirstPoint().y,
                    };
                    this.quadraticLines.push(firstLine);

                    const secondLine = new QuadraticLine(
                        this.getLastPoint().x,
                        this.getFirstPoint().y,
                        this.getLastPoint().x,
                        this.getLastPoint().y,
                        "vertical",
                    );

                    secondLine.potencial = {
                        x: this.getLastPoint().x,
                        y:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        xCreate: this.getLastPoint().x,
                        yCreate:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                    };
                    this.quadraticLines.push(secondLine);
                }
            } else {
                if (lastPlaceType === "horizontal") {
                    const firstLine = new QuadraticLine(
                        this.getFirstPoint().x,
                        this.getFirstPoint().y,
                        this.getFirstPoint().x,
                        this.getLastPoint().y,
                        "vertical",
                    );
                    firstLine.potencial = {
                        x: this.getFirstPoint().x,
                        y:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        xCreate: this.getFirstPoint().x,
                        yCreate:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                    };
                    this.quadraticLines.push(firstLine);

                    const secondLine = new QuadraticLine(
                        this.getFirstPoint().x,
                        this.getLastPoint().y,
                        this.getLastPoint().x,
                        this.getLastPoint().y,
                        "horizontal",
                    );

                    secondLine.potencial = {
                        x:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        y: this.getLastPoint().y,
                        xCreate:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        yCreate: this.getLastPoint().y,
                    };
                    this.quadraticLines.push(secondLine);
                } else {
                    const firstLine = new QuadraticLine(
                        this.getFirstPoint().x,
                        this.getFirstPoint().y,
                        this.getFirstPoint().x,
                        this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        "vertical",
                    );
                    firstLine.potencial = {
                        x: this.getFirstPoint().x,
                        y:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 4,
                        xCreate: this.getFirstPoint().x,
                        yCreate:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                    };
                    this.quadraticLines.push(firstLine);

                    const secondLine = new QuadraticLine(
                        this.getFirstPoint().x,
                        this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        this.getLastPoint().x,
                        this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        "horizontal",
                    );
                    secondLine.potencial = {
                        x:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        y:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        xCreate:
                            this.getFirstPoint().x +
                            (this.getLastPoint().x - this.getFirstPoint().x) / 2,
                        yCreate:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                    };
                    this.quadraticLines.push(secondLine);

                    const thirdLine = new QuadraticLine(
                        this.getLastPoint().x,
                        this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                        this.getLastPoint().x,
                        this.getLastPoint().y,
                        "vertical",
                    );
                    thirdLine.potencial = {
                        x: this.getLastPoint().x,
                        y:
                            this.getFirstPoint().y +
                            ((this.getLastPoint().y - this.getFirstPoint().y) / 4) * 3,
                        xCreate: this.getLastPoint().x,
                        yCreate:
                            this.getFirstPoint().y +
                            (this.getLastPoint().y - this.getFirstPoint().y) / 2,
                    };
                    this.quadraticLines.push(thirdLine);
                }
            }
        } else {
            // Добавляем первую линию
            if (this.points[1].quadraticType === "vertical") {
                const firstLine = new QuadraticLine(
                    this.getFirstPoint().x,
                    this.getFirstPoint().y,
                    this.points[1].x,
                    this.getFirstPoint().y,
                    "horizontal",
                );
                firstLine.potencial = {
                    x: firstLine.startX + (firstLine.endX - firstLine.startX) / 2,
                    y: firstLine.startY,
                    xCreate: firstLine.startX + (firstLine.endX - firstLine.startX) / 2,
                    yCreate: firstLine.startY,
                };
                this.quadraticLines.push(firstLine);
            } else {
                const firstLine = new QuadraticLine(
                    this.getFirstPoint().x,
                    this.getFirstPoint().y,
                    this.getFirstPoint().x,
                    this.points[1].y,
                    "vertical",
                );
                firstLine.potencial = {
                    x: firstLine.startX,
                    y: firstLine.startY + (firstLine.endY - firstLine.startY) / 2,
                    xCreate: firstLine.startX,
                    yCreate: firstLine.startY + (firstLine.endY - firstLine.startY) / 2,
                };
                this.quadraticLines.push(firstLine);
            }

            for (let i = 1; i < this.points.length - 1; i++) {
                if (this.points[i].quadraticType === "vertical") {
                    this.quadraticLines.push(
                        new QuadraticLine(
                            this.points[i].x,
                            this.points[i - 1].y,
                            this.points[i].x,
                            this.points[i + 1].y,
                            "vertical",
                        ),
                    );
                } else {
                    this.quadraticLines.push(
                        new QuadraticLine(
                            this.points[i - 1].x,
                            this.points[i].y,
                            this.points[i + 1].x,
                            this.points[i].y,
                            "horizontal",
                        ),
                    );
                }
            }

            // Добавляем последнюю линию
            if (this.points[this.points.length - 2].quadraticType === "vertical") {
                const firstLine = new QuadraticLine(
                    this.points[this.points.length - 2].x,
                    this.getLastPoint().y,
                    this.getLastPoint().x,
                    this.getLastPoint().y,
                    "horizontal",
                );
                firstLine.potencial = {
                    x: firstLine.startX + (firstLine.endX - firstLine.startX) / 2,
                    y: firstLine.startY,
                    xCreate: firstLine.startX + (firstLine.endX - firstLine.startX) / 2,
                    yCreate: firstLine.startY,
                };
                this.quadraticLines.push(firstLine);
            } else {
                const firstLine = new QuadraticLine(
                    this.getLastPoint().x,
                    this.points[this.points.length - 2].y,
                    this.getLastPoint().x,
                    this.getLastPoint().y,
                    "vertical",
                );
                firstLine.potencial = {
                    x: firstLine.startX,
                    y: firstLine.startY + (firstLine.endY - firstLine.startY) / 2,
                    xCreate: firstLine.startX,
                    yCreate: firstLine.startY + (firstLine.endY - firstLine.startY) / 2,
                };
                this.quadraticLines.push(firstLine);
            }
        }
    }

    repositionQuadraticPoints() {
        if (this.points.length > 2) {
            if (this.points[1].quadraticType === "vertical") {
                this.points[1].y = this.points[0].y + (this.points[2].y - this.points[0].y) / 2;
            } else {
                this.points[1].x = this.points[0].x + (this.points[2].x - this.points[0].x) / 2;
            }

            if (this.points[this.points.length - 2].quadraticType === "vertical") {
                this.points[this.points.length - 2].y =
                    this.points[this.points.length - 3].y +
                    (this.points[this.points.length - 1].y -
                        this.points[this.points.length - 3].y) /
                        2;
            } else {
                this.points[this.points.length - 2].x =
                    this.points[this.points.length - 3].x +
                    (this.points[this.points.length - 1].x -
                        this.points[this.points.length - 3].x) /
                        2;
            }
        }
    }

    shareRepositionTextItemsBezierMode() {
        let allWayLength = 0;
        for (let i = 1; i < this.points.length; i++) {
            const dx = this.points[i].x - this.points[i - 1].x;
            const dy = this.points[i].y - this.points[i - 1].y;
            allWayLength += Math.sqrt(dx * dx + dy * dy);
        }

        for (let textI = 0; textI < this.textItems.length; textI++) {
            const targetShare = this.textItems[textI].positionShare;

            let restWay = targetShare * allWayLength;
            for (let i = 1; i < this.points.length; i++) {
                const dx = this.points[i].x - this.points[i - 1].x;
                const dy = this.points[i].y - this.points[i - 1].y;
                const lineLength = Math.sqrt(dx * dx + dy * dy);

                if (restWay <= lineLength) {
                    const t = restWay / lineLength;
                    const p = this.points[i],
                        cp = this.cpoints[i],
                        cpp = this.cpoints[i - 1];

                    if (!cp?.cp || !cpp?.cn) continue;

                    const sharePoint = this.getBezierPoint(
                        this.points[i - 1].x,
                        this.points[i - 1].y,
                        cpp.cn[0],
                        cpp.cn[1],
                        cp.cp[0],
                        cp.cp[1],
                        p.x,
                        p.y,
                        t,
                    );
                    this.textItems[textI].x = sharePoint.x;
                    this.textItems[textI].y = sharePoint.y;
                    this.textItems[textI].updateTextPosition();
                    break;
                } else {
                    restWay -= lineLength;
                }
            }
        }
    }

    shareRepositionTextItemsQuadraticMode() {
        let allWayLength = 0;
        for (let i = 0; i < this.quadraticLines.length; i++) {
            allWayLength += this.quadraticLines[i].length;
        }

        for (let textI = 0; textI < this.textItems.length; textI++) {
            const targetShare = this.textItems[textI].positionShare;

            let restWay = targetShare * allWayLength;
            for (let i = 0; i < this.quadraticLines.length; i++) {
                const lineLength = this.quadraticLines[i].length;

                if (restWay <= lineLength) {
                    const t = restWay / lineLength;

                    if (this.quadraticLines[i].type === "horizontal") {
                        this.textItems[textI].x =
                            this.quadraticLines[i].startX +
                            t * (this.quadraticLines[i].endX - this.quadraticLines[i].startX);
                        this.textItems[textI].y = this.quadraticLines[i].startY;
                    } else {
                        this.textItems[textI].x = this.quadraticLines[i].startX;
                        this.textItems[textI].y =
                            this.quadraticLines[i].startY +
                            t * (this.quadraticLines[i].endY - this.quadraticLines[i].startY);
                    }
                    this.textItems[textI].updateTextPosition();

                    break;
                } else {
                    restWay -= lineLength;
                }
            }
        }
    }

    // search

    searchText(text: string) {
        const result: SearchTextObject[] = [];

        for (let i = 0; i < this.textItems.length; i++) {
            if (this.textItems[i].text) {
                const lineText = this.textItems[i].text?.getText().toLowerCase();

                if (lineText?.includes(text)) {
                    result.push(<SearchTextObject>{
                        status: true,
                        textWidget: this.textItems[i].text,
                        mode: "text",
                    });
                }
            }
        }

        return result;
    }

    // draws

    draw() {
        canv.ctx.save();

        this.drawTextItems();

        for (let i = 0; i < this.textItems.length; i++) {
            this.textItems[i].clip();
        }

        // Рисуем линию
        canv.ctx.strokeStyle = this.color;
        canv.ctx.lineWidth = this.lineWidth;

        if (this.dashType === "solid") {
            canv.ctx.setLineDash([]);
        } else if (this.dashType === "dashed") {
            canv.ctx.setLineDash([5 * this.lineWidth, 5 * this.lineWidth]);
        } else if (this.dashType === "dotted") {
            canv.ctx.lineCap = "round";
            canv.ctx.setLineDash([0.00001 * this.lineWidth, 5 * this.lineWidth]);
        }

        if (this.lineType === "curved" || this.lineType === "straight") {
            this.drawBezierMode();
        } else if (this.lineType === "quadratic") {
            this.drawQuadraticMode();
        }

        canv.ctx.setLineDash([]);
        canv.ctx.lineCap = "butt";

        canv.ctx.restore();
    }

    drawBezierMode() {
        canv.ctx.beginPath();
        canv.ctx.moveTo(this.points[0].x, this.points[0].y);

        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return;
            const p = this.points[i],
                cp = this.cpoints[i],
                cpp = this.cpoints[i - 1];
            // todo следить за if
            if (cpp.cn && cp.cp) {
                canv.ctx.bezierCurveTo(cpp.cn[0], cpp.cn[1], cp.cp[0], cp.cp[1], p.x, p.y);
            }
        }

        canv.ctx.stroke();
        canv.ctx.closePath();

        canv.ctx.setLineDash([]);
        canv.ctx.lineCap = "butt";
        this.drawArrowBezierMode("first");
        this.drawArrowBezierMode("last");
    }

    drawQuadraticMode() {
        canv.ctx.lineCap = "round";
        canv.ctx.beginPath();

        for (let i = 0; i < this.quadraticLines.length; i++) {
            canv.ctx.moveTo(this.quadraticLines[i].startX, this.quadraticLines[i].startY);
            canv.ctx.lineTo(this.quadraticLines[i].endX, this.quadraticLines[i].endY);
        }

        canv.ctx.stroke();
        canv.ctx.closePath();

        canv.ctx.setLineDash([]);
        canv.ctx.lineCap = "butt";
        this.drawArrowQuadraitcMode("first");
        this.drawArrowQuadraitcMode("last");
    }

    drawArrowBezierMode(side = "last") {
        const l = this.points.length;

        let mode = this.lastEndType;
        let bez = {
            sx: this.points[l - 2].x,
            sy: this.points[l - 2].y,

            cx1: this.cpoints[l - 2].cn![0],
            cy1: this.cpoints[l - 2].cn![1],

            cx2: this.cpoints[l - 1].cp![0],
            cy2: this.cpoints[l - 1].cp![1],

            ex: this.points[l - 1].x,
            ey: this.points[l - 1].y,
        };

        if (side === "first") {
            bez = {
                sx: this.points[1].x,
                sy: this.points[1].y,

                cx1: this.cpoints[1].cp![0],
                cy1: this.cpoints[1].cp![1],

                cx2: this.cpoints[0].cn![0],
                cy2: this.cpoints[0].cn![1],

                ex: this.points[0].x,
                ey: this.points[0].y,
            };

            mode = this.firstEndType;
        }

        if (mode === "none") return;

        const pointNearEnd = getCubicBezierXYatT(
            { x: bez.sx, y: bez.sy },
            { x: bez.cx1, y: bez.cy1 },
            { x: bez.cx2, y: bez.cy2 },
            { x: bez.ex, y: bez.ey },
            0.99,
        );
        const dx = bez.ex - pointNearEnd.x;
        const dy = bez.ey - pointNearEnd.y;
        const endingAngle = Math.atan2(dy, dx);

        canv.ctx.save();
        canv.ctx.beginPath();
        canv.ctx.strokeStyle = this.color;
        canv.ctx.fillStyle = this.color;
        canv.ctx.lineWidth = this.lineWidth;
        canv.ctx.lineCap = "butt";

        canv.ctx.translate(bez.ex, bez.ey);
        canv.ctx.rotate(endingAngle);

        if (mode === "filled") {
            this.drawFilledArrow();
        } else if (mode === "stroked") {
            this.drawStrokedArrow();
        } else if (mode === "rombus") {
            this.drawRombusArrow();
        } else if (mode === "circle") {
            this.drawCircleArrow();
        }

        canv.ctx.closePath();
        canv.ctx.restore();
    }

    drawArrowQuadraitcMode(side = "last") {
        canv.ctx.save();
        canv.ctx.beginPath();
        canv.ctx.strokeStyle = this.color;
        canv.ctx.fillStyle = this.color;
        canv.ctx.lineWidth = this.lineWidth;
        canv.ctx.lineCap = "butt";

        let mode = "filled";

        if (side === "last") {
            mode = this.lastEndType;
            canv.ctx.translate(this.getLastPoint().x, this.getLastPoint().y);
        } else {
            mode = this.firstEndType;
            canv.ctx.translate(this.getFirstPoint().x, this.getFirstPoint().y);
        }

        if (mode === "none") {
            canv.ctx.closePath();
            canv.ctx.restore();
            return;
        }

        let drawCertainArrow = this.drawFilledArrow.bind(this);

        if (mode === "stroked") {
            drawCertainArrow = this.drawStrokedArrow.bind(this);
        } else if (mode === "rombus") {
            drawCertainArrow = this.drawRombusArrow.bind(this);
        } else if (mode === "circle") {
            drawCertainArrow = this.drawCircleArrow.bind(this);
        }

        if (this.points.length === 2) {
            const firstPlaceType =
                this.firstPointPlace !== ""
                    ? this.firstPointPlace === "left" || this.firstPointPlace === "right"
                        ? "horizontal"
                        : "vertical"
                    : "horizontal";
            const lastPlaceType =
                this.lastPointPlace !== ""
                    ? this.lastPointPlace === "left" || this.lastPointPlace === "right"
                        ? "horizontal"
                        : "vertical"
                    : "horizontal";
            if (side === "last") {
                if (lastPlaceType === "horizontal") {
                    if (this.getLastPoint().x < this.getFirstPoint().x) {
                        canv.ctx.rotate(180 * (Math.PI / 180));
                    }
                    drawCertainArrow();
                } else {
                    if (this.getLastPoint().y > this.getFirstPoint().y) {
                        canv.ctx.rotate(90 * (Math.PI / 180));
                    } else {
                        canv.ctx.rotate(-90 * (Math.PI / 180));
                    }
                    drawCertainArrow();
                }
            } else {
                if (firstPlaceType === "horizontal") {
                    if (this.getLastPoint().x > this.getFirstPoint().x) {
                        canv.ctx.rotate(180 * (Math.PI / 180));
                    }
                    drawCertainArrow();
                } else {
                    if (this.getLastPoint().y > this.getFirstPoint().y) {
                        canv.ctx.rotate(-90 * (Math.PI / 180));
                    } else {
                        canv.ctx.rotate(90 * (Math.PI / 180));
                    }
                    drawCertainArrow();
                }
            }
        } else if (side === "first") {
            if (this.points[1].quadraticType === "vertical") {
                if (this.points[1].x > this.getFirstPoint().x) {
                    canv.ctx.rotate(180 * (Math.PI / 180));
                }
                drawCertainArrow();
            } else {
                if (this.points[1].y > this.getFirstPoint().y) {
                    canv.ctx.rotate(-90 * (Math.PI / 180));
                } else {
                    canv.ctx.rotate(90 * (Math.PI / 180));
                }
                drawCertainArrow();
            }
        } else {
            if (this.points[this.points.length - 2].quadraticType === "vertical") {
                if (this.points[this.points.length - 2].x > this.getLastPoint().x) {
                    canv.ctx.rotate(180 * (Math.PI / 180));
                }
                drawCertainArrow();
            } else {
                if (this.points[this.points.length - 2].y > this.getLastPoint().y) {
                    canv.ctx.rotate(-90 * (Math.PI / 180));
                } else {
                    canv.ctx.rotate(90 * (Math.PI / 180));
                }
                drawCertainArrow();
            }
        }

        canv.ctx.closePath();
        canv.ctx.restore();
    }

    // texts

    drawTextItems() {
        for (let i = 0; i < this.textItems.length; i++) {
            this.textItems[i].draw();
        }
    }

    extractTexts(): ExtractText[] {
        const texts: ExtractText[] = [];
        for (let i = 0; i < this.textItems.length; i++) {
            const text = this.textItems[i].get();
            if (text) {
                texts.push(text);
            }
        }
        return texts;
    }

    loadTexts(texts) {
        if (texts === undefined) return;
        this.textItems = [];
        for (let i = 0; i < texts.length; i++) {
            const newText = new LineTextItem(getNumberProperty("positionShare", texts[i]));
            if (newText.text) {
                newText.text.loadState(texts[i].text, "full");
            }
            this.textItems.push(newText);
        }
    }

    // Arrow Endings

    drawFilledArrow() {
        const k = this.lineWidth / 10;
        const normilizedK = easeOutCubic(k);
        const size = normilizedK * 30;
        canv.ctx.moveTo(-size, 0);
        canv.ctx.lineTo(-size, -size / 2);
        canv.ctx.lineTo(0, 0);
        canv.ctx.lineTo(-size, size / 2);
        canv.ctx.lineTo(-size, 0);
        canv.ctx.fill();
        canv.ctx.stroke();
    }

    drawStrokedArrow() {
        const k = this.lineWidth / 10;
        const normilizedK = easeOutCubic(k);
        const size = normilizedK * 30;
        canv.ctx.moveTo(-size, 0);
        canv.ctx.moveTo(-size, -size / 2);
        canv.ctx.lineTo(0, 0);
        canv.ctx.lineTo(-size, size / 2);
        canv.ctx.moveTo(-size, 0);
        canv.ctx.stroke();
    }

    drawRombusArrow() {
        const k = this.lineWidth / 10;
        const normilizedK = easeOutCubic(k);
        const size = normilizedK * 30;
        canv.ctx.moveTo(-size, 0);
        canv.ctx.lineTo(-size / 2, size / 2);
        canv.ctx.lineTo(0, 0);
        canv.ctx.lineTo(-size / 2, -size / 2);
        canv.ctx.lineTo(-size, 0);
        canv.ctx.fill();
        canv.ctx.stroke();
    }

    drawCircleArrow() {
        const k = this.lineWidth / 10;
        const normilizedK = easeOutCubic(k);
        const size = normilizedK * 30;
        canv.ctx.arc(-size / 2, 0, size / 2, 0, 2 * Math.PI);
        canv.ctx.fill();
        canv.ctx.stroke();
    }

    // CatchPointers and potencials

    updatePointerSize() {
        this.pointerSize = canv.actionManager.getZoomConstant(10);

        if (this.pointerSize < 4) {
            this.pointerSize = 4;
        }
    }

    showCatchPointer() {
        this.updatePointerSize();

        canv.ctx.fillStyle = "white";
        canv.ctx.strokeStyle = "#0B99FF";
        canv.ctx.lineWidth = this.pointerSize / 4;

        //  Рисуем точки
        for (let i = 0; i < this.points.length; i++) {
            canv.ctx.beginPath();
            canv.ctx.arc(this.points[i].x, this.points[i].y, this.pointerSize / 2, 0, 2 * Math.PI);
            canv.ctx.fill();
            canv.ctx.closePath();
            canv.ctx.beginPath();
            canv.ctx.arc(this.points[i].x, this.points[i].y, this.pointerSize / 2, 0, 2 * Math.PI);
            canv.ctx.stroke();
            canv.ctx.closePath();
        }

        // рисуем перспективные точки
        const memGlobalAlpha = canv.ctx.globalAlpha;
        canv.ctx.globalAlpha = 0.8;
        canv.ctx.fillStyle = "#0B99FF";

        if (this.lineType === "straight" || this.lineType === "curved") {
            this.showBezierPotencials();
        } else if (this.lineType === "quadratic") {
            this.showQuadraticPotencials();
        }

        canv.ctx.globalAlpha = memGlobalAlpha;
    }

    showBezierPotencials() {
        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return;
            const p = this.points[i],
                cp = this.cpoints[i],
                cpp = this.cpoints[i - 1];

            if (cpp.cn && cp.cp) {
                const middlePoint = this.getBezierPoint(
                    this.points[i - 1].x,
                    this.points[i - 1].y,
                    cpp.cn[0],
                    cpp.cn[1],
                    cp.cp[0],
                    cp.cp[1],
                    p.x,
                    p.y,
                );
                canv.ctx.beginPath();
                canv.ctx.arc(middlePoint.x, middlePoint.y, this.pointerSize / 2, 0, 2 * Math.PI);
                canv.ctx.fill();
            }
            //canv.ctx.bezierCurveTo(cpp.cn[0], cpp.cn[1], cp.cp[0], cp.cp[1], p["x"], p["y"]);
        }

        canv.ctx.closePath();
    }

    showQuadraticPotencials() {
        for (let i = 0; i < this.quadraticLines.length; i++) {
            if (this.quadraticLines[i].potencial != null) {
                canv.ctx.beginPath();
                canv.ctx.arc(
                    this.quadraticLines[i].potencial.x,
                    this.quadraticLines[i].potencial.y,
                    this.pointerSize / 2,
                    0,
                    2 * Math.PI,
                );
                canv.ctx.fill();
            }
        }
    }

    // Interaction -----------------------------------------------------------------------

    async TryAction(type) {
        if (type === "dblclick") {
            this.removeIntermediatePoint();
            return;
        }

        if (this.interactionType === "textMove") {
            if (type === "mousemove") {
                if (this.lineType === "curved" || this.lineType === "straight") {
                    this.moveTextBezierMode();
                } else if (this.lineType === "quadratic") {
                    this.moveTextQuadraticMode();
                }
                this.updateDims();
            } else if (type === "mouseup") {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mouseup");
            }
        } else if (this.interactionType === "pointMove") {
            if (type === "mousemove") {
                const pt = canv.actionManager.getActionPoint();
                const x = pt.x;
                const y = pt.y;

                if (this.lineType === "curved" || this.lineType === "straight") {
                    this.points[this.interactionPoint].x = x;
                    this.points[this.interactionPoint].y = y;
                    this.alignPoint(this.interactionPoint);
                } else if (this.lineType === "quadratic") {
                    if (this.points[this.interactionPoint].quadraticType === "horizontal") {
                        this.points[this.interactionPoint].y = y;
                        this.alignPoint(this.interactionPoint, "y");
                    } else {
                        this.points[this.interactionPoint].x = x;
                        this.alignPoint(this.interactionPoint, "x");
                    }

                    if (this.interactionPoint > 1) {
                        if (this.points[this.interactionPoint].quadraticType === "vertical") {
                            this.points[this.interactionPoint - 1].x =
                                this.points[this.interactionPoint - 2].x +
                                (this.points[this.interactionPoint].x -
                                    this.points[this.interactionPoint - 2].x) /
                                    2;
                        } else {
                            this.points[this.interactionPoint - 1].y =
                                this.points[this.interactionPoint - 2].y +
                                (this.points[this.interactionPoint].y -
                                    this.points[this.interactionPoint - 2].y) /
                                    2;
                        }
                    }

                    if (this.interactionPoint < this.points.length - 2) {
                        if (this.points[this.interactionPoint].quadraticType === "vertical") {
                            this.points[this.interactionPoint + 1].x =
                                this.points[this.interactionPoint + 2].x +
                                (this.points[this.interactionPoint].x -
                                    this.points[this.interactionPoint + 2].x) /
                                    2;
                        } else {
                            this.points[this.interactionPoint + 1].y =
                                this.points[this.interactionPoint + 2].y +
                                (this.points[this.interactionPoint].y -
                                    this.points[this.interactionPoint + 2].y) /
                                    2;
                        }
                    }
                }

                this.updateDims();
            } else if (type === "mouseup") {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
            }
        } else if (this.interactionType === "textFocus") {
            const focusedText = this.textItems[this.focusedText];

            if (focusedText) {
                await focusedText?.text?.TryAction(type);
                focusedText.updateTextPosition();
            }

            if (type === "mousedown" && !this.textItems[this.focusedText].text?.isOver()) {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
            }
        }
    }

    isInteraction(context, force) {
        if (context === "click") {
            if (this.isCatched()) {
                return { type: "none" };
            }

            const pt = canv.actionManager.getActionPoint();
            const x = pt.x;
            const y = pt.y;

            let status: boolean | undefined = false;

            // тексты
            status = this.isTextInteraction();

            if (status) {
                this.fixState("textMove");
                return { type: "interaction" };
            }

            // Перспективные точки
            if (this.lineType === "straight" || this.lineType === "curved") {
                status = this.isBezierPotencialsInteraction();
            } else if (this.lineType === "quadratic") {
                status = this.isQuadraticPotencialsInteraction();
            }

            if (status) {
                return { type: "interaction" };
            }

            // Уже имеющиеся точки
            for (let i = 0; i < this.points.length; i++) {
                const distance = Math.sqrt(
                    (this.points[i].x - x) * (this.points[i].x - x) +
                        (this.points[i].y - y) * (this.points[i].y - y),
                );
                if (distance <= this.pointerSize / 2) {
                    this.setInteractionPoint(i);
                    status = true;
                    break;
                }
            }

            if (status) {
                this.fixState("pointMove");
                return { type: "interaction" };
            }

            return { type: "none" };
        } else if (force && context === "") {
            // сюда попадаем когда добавляем текст через widget menu
            return { type: "interaction" };
        } else if (context === "focus") {
            for (let i = 0; i < this.textItems.length; i++) {
                if (this.textItems[i].isOver()) {
                    this.fixState("textFocus");
                    this.focusedText = i;
                    this.interactionType = "textFocus";
                    this.bindFocusedTextAutosave(this.textItems[this.focusedText].text);
                    this.textItems[this.focusedText].text?.focusText(true);
                    return { type: "interaction" };
                }
            }
        }

        return { type: "none" };
    }

    stopInteraction() {
        canv.eventManager.openRecord();

        if (this.interactionType === "textMove") {
            this.saveState("textMove");
        } else if (this.interactionType === "pointMove") {
            this.saveState("pointMove");
        } else if (this.interactionType === "textFocus") {
            const focusedText = this.textItems[this.focusedText].text;
            if (focusedText) {
                const isEmptyText = focusedText.symbolsCount === 0;
                const shouldSave = isEmptyText || !focusedText.consumeHostInteractionSaveSkip();

                focusedText.unfocusText();
                if (isEmptyText) {
                    this.textItems.splice(this.focusedText, 1);
                }
                if (shouldSave) {
                    this.saveState("textFocus");
                }
            }
        }

        canv.eventManager.closeRecord();

        this.interactionType = "";
    }

    // -------------------------------------------------------------------------

    moveTextBezierMode() {
        let allWayLength = 0;
        for (let i = 1; i < this.points.length; i++) {
            const dx = this.points[i].x - this.points[i - 1].x;
            const dy = this.points[i].y - this.points[i - 1].y;
            allWayLength += Math.sqrt(dx * dx + dy * dy);
        }

        let minDistance: undefined | number;

        // какую часть занимают уже расмотренные линии
        let wayGoneShare = 0;

        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return false;
            const dx = this.points[i].x - this.points[i - 1].x;
            const dy = this.points[i].y - this.points[i - 1].y;
            const lineLength = Math.sqrt(dx * dx + dy * dy);

            const p = this.points[i],
                cp = this.cpoints[i],
                cpp = this.cpoints[i - 1];

            // todo проследить что ничего не сломал if
            if (cpp.cn && cp.cp) {
                const res = this.getClosestPointBezierLine(
                    this.points[i - 1].x,
                    this.points[i - 1].y,
                    cpp.cn[0],
                    cpp.cn[1],
                    cp.cp[0],
                    cp.cp[1],
                    p.x,
                    p.y,
                );

                if (minDistance === undefined || (res.distance && res.distance < minDistance)) {
                    this.textItems[this.interactionText].x = res.point.x;
                    this.textItems[this.interactionText].y = res.point.y;
                    const trueShare = wayGoneShare + (lineLength / allWayLength) * res.share;
                    this.textItems[this.interactionText].positionShare = trueShare;
                    minDistance = res.distance;
                }

                wayGoneShare += lineLength / allWayLength;
            }
        }
    }

    getClosestPointBezierLine(x1, y1, x2, y2, x3, y3, x4, y4): ClosestPointResult {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const closest: ClosestPointResult = {
            distance: undefined,
            point: { x: 0, y: 0 },
            share: 0.5,
        };

        const iterates = 1000;
        const step = 1 / iterates;
        for (let t = 0; t < 1; t += step) {
            const res = this.getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t);
            const distance = Math.sqrt((res.x - x) * (res.x - x) + (res.y - y) * (res.y - y));
            if (closest.distance === undefined || distance < closest.distance) {
                closest.distance = distance;
                closest.point = res;
                closest.share = t;
            }
        }

        return closest;
    }

    getPointIndex() {
        this.updatePointerSize();

        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        for (let i = 0; i < this.points.length; i++) {
            const distance = Math.sqrt((this.points[i].x - x) ** 2 + (this.points[i].y - y) ** 2);

            if (distance <= this.pointerSize / 2) {
                return i;
            }
        }

        return -1;
    }

    removeIntermediatePoint() {
        const pointIndex = this.getPointIndex();
        const isIntermediatePoint = pointIndex > 0 && pointIndex < this.points.length - 1;

        if (!isIntermediatePoint || this.points.length <= 2) {
            return false;
        }

        canv.eventManager.openRecord();
        this.fixState("pointMove");
        this.points.splice(pointIndex, 1);
        this.interactionPoint = Math.min(pointIndex, this.points.length - 1);
        this.interactionType = "";
        this.catchPointer = "";
        this.updateDims();
        this.saveState("pointMove");
        canv.eventManager.closeRecord();

        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        canv.redraw();

        return true;
    }

    moveTextQuadraticMode() {
        let allWayLength = 0;
        for (let i = 0; i < this.quadraticLines.length; i++) {
            allWayLength += this.quadraticLines[i].length;
        }

        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        let minDistance: undefined | number;
        let wayGoneShare = 0;

        for (let i = 0; i < this.quadraticLines.length; i++) {
            const lineLength = this.quadraticLines[i].length;

            if (this.quadraticLines[i].type === "horizontal") {
                const leftX =
                    this.quadraticLines[i].startX < this.quadraticLines[i].endX
                        ? this.quadraticLines[i].startX
                        : this.quadraticLines[i].endX;
                const rightX =
                    this.quadraticLines[i].startX > this.quadraticLines[i].endX
                        ? this.quadraticLines[i].startX
                        : this.quadraticLines[i].endX;
                if (x >= leftX && x <= rightX) {
                    // другой случай не рассматриваем, так как текст уже будет в углу
                    const distance = Math.abs(this.quadraticLines[i].startY - y);
                    const linePositionShare =
                        Math.abs(x - this.quadraticLines[i].startX) / lineLength; // res["share"] для bezierMode

                    if (minDistance === undefined || distance < minDistance) {
                        // Обновляем x, y только в shareReposition...
                        //this.textItems[this.interactionText].x = x;
                        //this.textItems[this.interactionText].y = this.quadraticLines[i].startY;
                        const trueShare =
                            wayGoneShare + (lineLength / allWayLength) * linePositionShare;
                        this.textItems[this.interactionText].positionShare = trueShare;
                        minDistance = distance;
                    }
                }
            } else {
                const topY =
                    this.quadraticLines[i].startY < this.quadraticLines[i].endY
                        ? this.quadraticLines[i].startY
                        : this.quadraticLines[i].endY;
                const bottomY =
                    this.quadraticLines[i].startY > this.quadraticLines[i].endY
                        ? this.quadraticLines[i].startY
                        : this.quadraticLines[i].endY;
                if (y >= topY && y <= bottomY) {
                    // другой случай не рассматриваем, так как текст уже будет в углу
                    const distance = Math.abs(this.quadraticLines[i].startX - x);
                    const linePositionShare =
                        Math.abs(y - this.quadraticLines[i].startY) / lineLength; // res["share"] для bezierMode
                    if (minDistance === undefined || distance < minDistance) {
                        // Обновляем x, y только в shareReposition...
                        //this.textItems[this.interactionText].x = this.quadraticLines[i].startX;
                        //this.textItems[this.interactionText].y = y;
                        const trueShare =
                            wayGoneShare + (lineLength / allWayLength) * linePositionShare;
                        this.textItems[this.interactionText].positionShare = trueShare;
                        minDistance = distance;
                    }
                }
            }

            wayGoneShare += lineLength / allWayLength;
        }
    }

    // helpers

    getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t = 0.5) {
        const result = {
            x: 0,
            y: 0,
        };

        result.x =
            (1 - t) * (1 - t) * (1 - t) * x1 +
            3 * (1 - t) * (1 - t) * t * x2 +
            3 * (1 - t) * t * t * x3 +
            t * t * t * x4;
        result.y =
            (1 - t) * (1 - t) * (1 - t) * y1 +
            3 * (1 - t) * (1 - t) * t * y2 +
            3 * (1 - t) * t * t * y3 +
            t * t * t * y4;
        return result;
    }

    getBezierDims(x1, y1, x2, y2, x3, y3, x4, y4) {
        const total = {
            minX: this.getFirstPoint().x,
            maxX: this.getFirstPoint().x,
            minY: this.getFirstPoint().y,
            maxY: this.getFirstPoint().y,
        };

        const iterates = 1000;
        const step = 1 / iterates;
        for (let t = 0; t < 1; t += step) {
            const res = this.getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t);

            total.minX = res.x < total.minX ? res.x : total.minX;
            total.maxX = res.x > total.maxX ? res.x : total.maxX;
            total.minY = res.y < total.minY ? res.y : total.minY;
            total.maxY = res.y > total.maxY ? res.y : total.maxY;
        }

        return total;
    }

    createPointAndInteract(i, x, y, quadraticType: LineDirection = "horizontal") {
        this.fixState("pointMove");
        const newPoint = new LinePoint(x, y, quadraticType);
        this.points.splice(i, 0, newPoint);
        this.setInteractionPoint(i);
        this.updateDims();
    }

    extractPoints() {
        const res: LinePoint[] = [];
        for (let i = 0; i < this.points.length; i++) {
            const point = {
                x: this.points[i].x,
                y: this.points[i].y,
                quadraticType: this.points[i].quadraticType,
            };
            res.push(point);
        }
        return res;
    }

    loadPoints(points) {
        if (points === undefined) return;
        this.points = [];
        for (let i = 0; i < points.length; i++) {
            const x = getNumberProperty("x", points[i]);
            const y = getNumberProperty("y", points[i]);
            const quadraticType = getStringProperty("quadraticType", points[i]) as LineDirection;
            const newPoint = new LinePoint(x, y, quadraticType);
            this.points.push(newPoint);
        }
    }

    drawQuadraticLineBetween(i, type) {
        if (type === "horizontal") {
            canv.ctx.lineTo(this.points[i - 1].x, this.points[i].y);
        } else {
            canv.ctx.lineTo(this.points[i].x, this.points[i - 1].y);
        }
        canv.ctx.lineTo(this.points[i].x, this.points[i].y);
    }

    setInteractionPoint(i) {
        this.interactionPoint = i;
        this.interactionType = "pointMove";
    }

    setInteractionText(i) {
        this.interactionText = i;
        this.interactionType = "textMove";
    }

    // Selecting helpers

    isOverBezierLine(x1, y1, x2, y2, x3, y3, x4, y4) {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const gap = canv.actionManager.getZoomConstant(5);

        const iterates = 1000;
        const step = 1 / iterates;
        for (let t = 0; t < 1; t += step) {
            const res = this.getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t);
            const distance = Math.sqrt((res.x - x) * (res.x - x) + (res.y - y) * (res.y - y));
            if (distance <= this.lineWidth / 2 + gap) {
                return true;
            }
        }

        return false;
    }

    isIntersectBezierLine(x1, y1, x2, y2, x3, y3, x4, y4, area) {
        const iterates = 1000;
        const step = 1 / iterates;
        for (let t = 0; t < 1; t += step) {
            const res = this.getBezierPoint(x1, y1, x2, y2, x3, y3, x4, y4, t);
            if (
                res.x > area.x &&
                res.x < area.x + area.width &&
                res.y > area.y &&
                res.y < area.y + area.height
            ) {
                return true;
            }
        }

        return false;
    }

    isOverVerticalLine(lineX, startY, endY) {
        const topY = startY < endY ? startY : endY;
        const bottomY = startY > endY ? startY : endY;
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const gap = canv.actionManager.getZoomConstant(5);

        return (
            x > lineX - this.lineWidth / 2 - gap &&
            x < lineX + this.lineWidth / 2 + gap &&
            y > topY &&
            y < bottomY
        );
    }

    isOverHorizontalLine(lineY, startX, endX) {
        const leftX = startX < endX ? startX : endX;
        const rightX = startX > endX ? startX : endX;
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const gap = canv.actionManager.getZoomConstant(5);

        return (
            x > leftX &&
            x < rightX &&
            y > lineY - this.lineWidth / 2 - gap &&
            y < lineY + this.lineWidth / 2 + gap
        );
    }

    isVerticalLineInArea(lineX, startY, endY, area) {
        const topY = startY < endY ? startY : endY;
        const bottomY = startY > endY ? startY : endY;

        return (
            area.x <= lineX + this.lineWidth / 2 &&
            area.y <= bottomY &&
            area.x + area.width >= lineX - this.lineWidth / 2 &&
            area.y + area.height >= topY
        );
    }

    isHorizontalLineInArea(lineY, startX, endX, area) {
        const leftX = startX < endX ? startX : endX;
        const rightX = startX > endX ? startX : endX;

        return (
            area.x <= rightX &&
            area.y <= lineY + this.lineWidth &&
            area.x + area.width >= leftX &&
            area.y + area.height >= lineY - this.lineWidth / 2
        );
    }

    // interaction

    isBezierPotencialsInteraction() {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        for (let i = 1; i < this.points.length; i++) {
            const p = this.points[i],
                cp = this.cpoints[i],
                cpp = this.cpoints[i - 1];

            // todo проследить за работой continue
            if (!cp?.cp || !cpp?.cn) continue;

            const middlePoint = this.getBezierPoint(
                this.points[i - 1].x,
                this.points[i - 1].y,
                cpp.cn[0],
                cpp.cn[1],
                cp.cp[0],
                cp.cp[1],
                p.x,
                p.y,
            );
            const distance = Math.sqrt(
                (middlePoint.x - x) * (middlePoint.x - x) +
                    (middlePoint.y - y) * (middlePoint.y - y),
            );
            if (distance <= this.pointerSize / 2) {
                this.createPointAndInteract(i, middlePoint.x, middlePoint.y);
                return true;
            }
        }

        return false;
    }

    isQuadraticPotencialsInteraction() {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        for (let i = 0; i < this.quadraticLines.length; i++) {
            if (this.quadraticLines[i].potencial == null) continue;
            const distance = Math.sqrt(
                (this.quadraticLines[i].potencial.x - x) *
                    (this.quadraticLines[i].potencial.x - x) +
                    (this.quadraticLines[i].potencial.y - y) *
                        (this.quadraticLines[i].potencial.y - y),
            );

            if (distance <= this.pointerSize / 2) {
                if (this.points.length !== 2) {
                    // тут potensials.length == 2 всегда
                    // тут potensials[0] - это это потенциал рядом с первой точкой, а potensials[1] это потенциал рядом с последней точкой
                    if (i === 0) {
                        this.createPointAndInteract(
                            1,
                            this.quadraticLines[i].potencial.xCreate,
                            this.quadraticLines[i].potencial.yCreate,
                            this.quadraticLines[i].type,
                        );
                    } else {
                        this.createPointAndInteract(
                            this.points.length - 1,
                            this.quadraticLines[i].potencial.xCreate,
                            this.quadraticLines[i].potencial.yCreate,
                            this.quadraticLines[i].type,
                        );
                    }
                } else {
                    this.createPointAndInteract(
                        1,
                        this.quadraticLines[i].potencial.xCreate,
                        this.quadraticLines[i].potencial.yCreate,
                        this.quadraticLines[i].type,
                    );
                }

                return true;
            }
        }
    }

    isTextInteraction() {
        for (let i = 0; i < this.textItems.length; i++) {
            if (this.textItems[i].isOver()) {
                this.setInteractionText(i);
                return true;
            }
        }

        return false;
    }

    // iss

    isCatched() {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        if (
            x > this.getFirstPoint().x - this.pointerSize / 2 &&
            x < this.getFirstPoint().x + this.pointerSize / 2 &&
            y > this.getFirstPoint().y - this.pointerSize / 2 &&
            y < this.getFirstPoint().y + this.pointerSize / 2
        ) {
            this.catchPointer = "firstPoint";
            return true;
        }

        if (
            x > this.getLastPoint().x - this.pointerSize / 2 &&
            x < this.getLastPoint().x + this.pointerSize / 2 &&
            y > this.getLastPoint().y - this.pointerSize / 2 &&
            y < this.getLastPoint().y + this.pointerSize / 2
        ) {
            this.catchPointer = "lastPoint";
            return true;
        }

        return false;
    }

    isOver() {
        for (let i = 0; i < this.textItems.length; i++) {
            if (this.textItems[i].isOver()) {
                return true;
            }
        }

        if (this.lineType === "straight" || this.lineType === "curved") {
            return this.isOverBezierMode();
        } else if (this.lineType === "quadratic") {
            return this.isOverQuadraticMode();
        }

        return false;
    }

    isOverBezierMode() {
        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return false;
            const p = this.points[i],
                cp = this.cpoints[i],
                cpp = this.cpoints[i - 1];

            if (cpp.cn && cp.cp) {
                const res = this.isOverBezierLine(
                    this.points[i - 1].x,
                    this.points[i - 1].y,
                    cpp.cn[0],
                    cpp.cn[1],
                    cp.cp[0],
                    cp.cp[1],
                    p.x,
                    p.y,
                );

                if (res) {
                    return true;
                }
            }
        }

        return false;
    }

    isOverQuadraticMode() {
        for (let i = 0; i < this.quadraticLines.length; i++) {
            if (this.quadraticLines[i].type === "horizontal") {
                if (
                    this.isOverHorizontalLine(
                        this.quadraticLines[i].startY,
                        this.quadraticLines[i].startX,
                        this.quadraticLines[i].endX,
                    )
                ) {
                    return true;
                }
            } else {
                if (
                    this.isOverVerticalLine(
                        this.quadraticLines[i].startX,
                        this.quadraticLines[i].startY,
                        this.quadraticLines[i].endY,
                    )
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    isInArea(area: BezierArea): boolean {
        if (this.lineType === "straight" || this.lineType === "curved") {
            return this.isInAreaBezierMode(area);
        } else if (this.lineType === "quadratic") {
            return this.isInAreaQuadraticMode(area);
        }

        return false;
    }

    isInAreaBezierMode(area) {
        for (let i = 1; i < this.points.length; i++) {
            if (this.points.length !== this.cpoints.length) return false;
            const p = this.points[i],
                cp = this.cpoints[i],
                cpp = this.cpoints[i - 1];

            // todo проследить за работой continue
            if (!cp?.cp || !cpp?.cn) continue;

            const res = this.isIntersectBezierLine(
                this.points[i - 1].x,
                this.points[i - 1].y,
                cpp.cn[0],
                cpp.cn[1],
                cp.cp[0],
                cp.cp[1],
                p.x,
                p.y,
                area,
            );

            if (res) {
                return true;
            }
        }

        return false;
    }

    isInAreaQuadraticMode(area) {
        for (let i = 0; i < this.quadraticLines.length; i++) {
            if (this.quadraticLines[i].type === "horizontal") {
                if (
                    this.isHorizontalLineInArea(
                        this.quadraticLines[i].startY,
                        this.quadraticLines[i].startX,
                        this.quadraticLines[i].endX,
                        area,
                    )
                ) {
                    return true;
                }
            } else {
                if (
                    this.isVerticalLineInArea(
                        this.quadraticLines[i].startX,
                        this.quadraticLines[i].startY,
                        this.quadraticLines[i].endY,
                        area,
                    )
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    TryPointMaster(point) {
        for (let i = canv.canvasController.items.length - 1; i >= 0; i--) {
            const res = canv.canvasController.items[i].hasArrow(this.UID, point);
            if (res != null) {
                return res;
            }
        }

        return null;
    }

    translate(x, y) {
        const firstPointMaster = this.TryPointMaster("firstPoint");
        const lastPointMaster = this.TryPointMaster("lastPoint");

        let firstPoint = 0;
        let lastPoint = this.points.length - 1;

        if (firstPointMaster != null) {
            firstPoint++;
        }

        if (lastPointMaster != null) {
            lastPoint--;
        }

        for (let i = firstPoint; i <= lastPoint; i++) {
            this.points[i].x += x;
            this.points[i].y += y;
        }

        this.updateDims();
    }

    resize(x: number, y: number, catchPointer: string) {
        if (catchPointer !== "firstPoint" && catchPointer !== "lastPoint") {
            return;
        }

        let magneted = false;
        const items = canv.canvasController.hashGrid?.queryByViewport() || [];

        if (catchPointer === "firstPoint") {
            for (let i = items.length - 1; i >= 0; i--) {
                const res = items[i].TryMagnet(this, "firstPoint");

                if (res.status) {
                    this.firstPointPlace = res.place;
                    this.getFirstPoint().x = res.x;
                    this.getFirstPoint().y = res.y;
                    magneted = true;
                }
            }

            if (!magneted) {
                const pt = canv.actionManager.getActionPoint();

                this.getFirstPoint().x = pt.x;
                this.getFirstPoint().y = pt.y;
                this.alignPoint(0);
            }

            this.updateDims();
        } else if (catchPointer === "lastPoint") {
            if (fastActionManager.fastActionGroup == null) {
                this.lastPointPlace = "";
            } else {
                if (fastActionManager?.setupFastActionGroup) {
                    this.lastPointPlace = fastActionManager?.setupFastActionGroup() || "";
                }
            }

            for (let i = items.length - 1; i >= 0; i--) {
                const res = items[i].TryMagnet(this, "lastPoint");

                if (res.status) {
                    this.lastPointPlace = res.place;
                    this.getLastPoint().x = res.x;
                    this.getLastPoint().y = res.y;
                    magneted = true;
                }
            }

            if (!magneted) {
                const pt = canv.actionManager.getActionPoint();

                this.getLastPoint().x = pt.x;
                this.getLastPoint().y = pt.y;
                this.alignPoint(this.points.length - 1);
            }

            this.updateDims();
        }
    }

    alignPoint(index, mayChange = "") {
        const point = this.points[index];

        const gap = canv.actionManager.getZoomConstant(10);

        const mayChangeX = mayChange === "" || mayChange === "x";
        const mayChangeY = mayChange === "" || mayChange === "y";

        for (let i = 0; i < this.points.length; i++) {
            if (i === index) continue;

            if (
                mayChangeX &&
                point.x >= this.points[i].x - gap &&
                point.x <= this.points[i].x + gap
            ) {
                point.x = this.points[i].x;
            }

            if (
                mayChangeY &&
                point.y >= this.points[i].y - gap &&
                point.y <= this.points[i].y + gap
            ) {
                point.y = this.points[i].y;
            }
        }
    }

    // resizingStop beforeDeletion

    detachPointFromMaster(point: "firstPoint" | "lastPoint") {
        const pointMaster = this.TryPointMaster(point);

        pointMaster?.TryRemoveArrow(this, point);
    }

    resizingStop(catchPointer: string) {
        const items = canv.canvasController.hashGrid?.queryByViewport() || [];

        if (catchPointer === "firstPoint") {
            this.detachPointFromMaster("firstPoint");
            this.firstPointPlace = "";

            for (let i = items.length - 1; i >= 0; i--) {
                const res = items[i].TryMagnet(this, "firstPoint", true);

                if (res.status) {
                    this.firstPointPlace = res.place;
                    this.getFirstPoint().x = res.x;
                    this.getFirstPoint().y = res.y;
                }
            }
        } else if (catchPointer === "lastPoint") {
            this.detachPointFromMaster("lastPoint");
            this.lastPointPlace = "";

            for (let i = items.length - 1; i >= 0; i--) {
                const res = items[i].TryMagnet(this, "lastPoint", true);

                if (res.status) {
                    this.lastPointPlace = res.place;
                    this.getLastPoint().x = res.x;
                    this.getLastPoint().y = res.y;
                }
            }
        }

        this.updateDims();
    }

    beforeDeletion() {
        for (let i = canv.canvasController.items.length - 1; i >= 0; i--) {
            canv.canvasController.items[i].TryRemoveArrowRecursively(this, "firstPoint");
            canv.canvasController.items[i].TryRemoveArrowRecursively(this, "lastPoint");
        }
    }

    getFirstPoint() {
        return this.points[0];
    }

    getLastPoint() {
        return this.points[this.points.length - 1];
    }

    // Далее для menu

    menuColor(color) {
        this.color = color;
        const arrowTool = canv.toolBox.TryGetToolByName("arrowTool");
        arrowTool.setColor(this.color);
    }

    menuLineType(data) {
        if (data.lineWidth !== undefined) {
            this.lineWidth = data.lineWidth;
        } else if (data.lineType !== undefined) {
            if (data.lineType === "quadratic") {
                const firstPoint = this.points[0];
                const secondPoint = this.points[this.points.length - 1];
                this.points = [];
                this.points.push(firstPoint);
                this.points.push(secondPoint);
            }
            this.lineType = data.lineType;
        } else if (data.dashType !== undefined) {
            this.dashType = data.dashType;
        }

        const arrowTool = canv.toolBox.TryGetToolByName("arrowTool");
        arrowTool.setLineWidth(this.lineWidth);
        arrowTool.setLineType(this.lineType);
        arrowTool.setDashType(this.dashType);

        this.updateDims();
    }

    menuEndType(info) {
        if (info.side === "first") {
            this.firstEndType = info.endType;
        } else {
            this.lastEndType = info.endType;
        }

        const arrowTool = canv.toolBox.TryGetToolByName("arrowTool");
        arrowTool.setFirstEndType(this.firstEndType);
        arrowTool.setLastEndType(this.lastEndType);
    }

    menuAddText() {
        // нужно тут сбросить mode, а не просто stopInteraction, иначем setSelectedAndInteraction потом сбросит interactionType.
        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");

        // Добавляем lineTextItem. Добавление сохранит widgetMenu с контекстом wm_addText
        this.textItems.push(new LineTextItem(0.5));
        this.focusedText = this.textItems.length - 1;
        this.interactionType = "textFocus";
        this.bindFocusedTextAutosave(this.textItems[this.focusedText].text);
        this.textItems[this.focusedText].text?.focusText();
        this.updateDims();

        // запоминаем стейт в контексте textFocus
        this.fixState("textFocus");

        canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(this.parent);
    }

    menuTextSize(size: number) {
        if (this.interactionType === "textFocus") {
            if (this.textItems[this.focusedText].text) {
                this.textItems[this.focusedText]?.text?.menuTextSize(size);
            }
        } else {
            for (let i = 0; i < this.textItems.length; i++) {
                if (this.textItems[i].text) {
                    this.textItems[i].text?.menuTextSize(size);
                }
            }
        }
        this.updateDims();
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];
        widgetOptions.push(new widgetOption("color", this.color, this.UID));
        widgetOptions.push(new widgetOption("arrowEndFirst", this.firstEndType, this.UID));

        const wmLineTypeInfo = {
            lineWidth: this.lineWidth,
            lineType: this.lineType,
            dashType: this.dashType,
        };

        widgetOptions.push(new widgetOption("lineType", wmLineTypeInfo, this.UID));

        widgetOptions.push(new widgetOption("arrowEnd", this.lastEndType, this.UID));

        widgetOptions.push(new widgetOption("addText", {}, this.UID));

        if (this.textItems.length !== 0) {
            widgetOptions.push(
                new widgetOption("textSize", this.textItems[0].text?.fontSize || 1, this.UID),
            );
        }

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "color") {
            return this.menuColor;
        } else if (name === "arrowEndFirst") {
            return this.menuEndType;
        } else if (name === "arrowEnd") {
            return this.menuEndType;
        } else if (name === "lineType") {
            return this.menuLineType;
        } else if (name === "addText") {
            return this.menuAddText;
        } else if (name === "textSize") {
            return this.menuTextSize;
        }
    }

    // для statesManager

    getFixType(reason) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
            case "resize":
                fixType = "resize";
                break;
            case "wm_color":
                fixType = "color";
                break;
        }

        return fixType;
    }

    getState<FixType extends keyof WidgetStateMap>(fixType: FixType): WidgetStateMap[FixType] {
        const state: WidgetStateMap = {
            move: {
                points: this.extractPoints(),
            },
            resize: {
                points: this.extractPoints(),
                firstPointPlace: this.firstPointPlace,
                lastPointPlace: this.lastPointPlace,
            },
            color: {
                color: this.color,
            },
            full: {
                UID: this.UID,
                parentUID: this.parentUID,
                entityType: "arrowWidget",
                points: this.extractPoints(),
                texts: this.extractTexts(),
                firstPointPlace: this.firstPointPlace,
                lastPointPlace: this.lastPointPlace,
                color: this.color,
                lineWidth: this.lineWidth,
                lineType: this.lineType,
                dashType: this.dashType,
                firstEndType: this.firstEndType,
                lastEndType: this.lastEndType,
            },
        };

        return state[fixType];
    }

    loadState(stateObj, fixType) {
        switch (fixType) {
            case "move":
                this.loadPoints(stateObj.points);
                break;
            case "resize":
                this.loadPoints(stateObj.points);
                this.firstPointPlace = getStringProperty("firstPointPlace", stateObj);
                this.lastPointPlace = getStringProperty("lastPointPlace", stateObj) as ArrowPlace;
                break;
            case "color":
                this.color = getColorProperty("color", stateObj);
                break;
            case "lineWidth":
                this.lineWidth = getNumberProperty("lineWidth", stateObj);
                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.loadPoints(stateObj.points);
                this.loadTexts(stateObj.texts);
                this.firstPointPlace = getStringProperty("firstPointPlace", stateObj);
                this.lastPointPlace = getStringProperty("lastPointPlace", stateObj) as ArrowPlace;
                this.color = getColorProperty("color", stateObj);
                this.lineWidth = getNumberProperty("lineWidth", stateObj);
                this.lineType = getStringProperty("lineType", stateObj, "curved");
                this.dashType = getStringProperty("dashType", stateObj, "solid");
                this.firstEndType = getStringProperty("firstEndType", stateObj, "filled");
                this.lastEndType = getStringProperty("lastEndType", stateObj, "filled");
                break;
        }

        this.updateDims();
    }

    constructorName: "arrow" = "arrow";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;

    firstPointPlace = "";
    lastPointPlace: ArrowPlace = "";
    catchPointer: string = "";

    cpoints: CurvePoint[] = [];
    points: LinePoint[] = [];
    quadraticLines: QuadraticLine[] = [];
    textItems: LineTextItem[] = [];
    interactionPoint = 0;
    interactionText = 0;
    interactionType = "pointMove";
    focusedText = 0;

    lineWidth = 0;
    color = "black";
    lineType = "curved";
    dashType = "solid";
    firstEndType = "none";
    lastEndType = "filled";

    allowOptionsForLocked = true;
}
